// Refine +1 a +30: escolhe um equipamento carregado, vê a chance e o custo e tenta (com ou sem pedra de proteção).
// Visual de forja: bigorna, brasas, laranja incandescente e metal escuro. CSS em client/css-parts/refine.css (prefixo .rn-).
import { useEffect, useState } from 'preact/hooks';
import type { ItemInstance } from '../../../../shared/src/game/items';
import { upgradeText } from '../../../../shared/src/game/items';
import type { Extras } from '../../../../shared/src/game/extras';
import { FRAGMENTS_PER_STONE, REFINABLE_SLOTS, REFINE_DROP_CHANCE, REFINE_MAX, REFINE_PROTECT_MAX, protectMult, refineChance, refineCost } from '../../../../shared/src/game/refine';
import { catalogNow } from '../items';
import { ItemImg } from './ItemSlot';

const n = (v: number) => v.toLocaleString('pt-BR');
const pct = (v: number) => String(v).replace('.', ',') + '%';
/** gold compacto para a escala (10 mil, 1,2 mi) */
const short = (v: number) => v >= 1e6 ? (Math.round(v / 1e5) / 10).toString().replace('.', ',') + ' mi' : v >= 1e3 ? Math.round(v / 1e3) + ' mil' : String(v);
/** faixa de risco pela chance: verde, amarelo ou vermelho */
const risk = (c: number) => (c >= 50 ? 'safe' : c >= 20 ? 'mid' : 'hard');
const RISK_TXT: Record<string, string> = { safe: 'Boa chance', mid: 'Arriscado', hard: 'Muito difícil' };
/** faixas de níveis da escala (seguem as faixas de chance do refine) */
const BANDS: Array<{ from: number; to: number; name: string; cls: string }> = [
  { from: 1, to: 5, name: 'Ferro', cls: 'b1' }, { from: 6, to: 10, name: 'Aço', cls: 'b2' }, { from: 11, to: 20, name: 'Mithril', cls: 'b3' },
  { from: 21, to: 25, name: 'Rúnico', cls: 'b4' }, { from: 26, to: 30, name: 'Lendário', cls: 'b5' },
];
const bandOf = (lvl: number) => BANDS.find((b) => lvl >= b.from && lvl <= b.to) ?? BANDS[0]!;

type Outcome = 'ok' | 'drop' | 'saved' | 'kept';
interface LastResult { name: string; typeId: number; from: number; to: number; outcome: Outcome; at: number }

export interface RefineResult { itemId: number; from: number; to: number; outcome: Outcome; at: number }
export function Refine(props: { items: ItemInstance[]; bank: number; extras: Extras; onRefine: (itemId: number, protect: boolean) => void; result?: RefineResult | null }) {
  const cat = catalogNow();
  const byId = new Map(props.items.map((i) => [i.id, i]));
  const carried = (i: ItemInstance) => { let c: ItemInstance | undefined = i; while (c && c.place === 'container') c = c.parentId ? byId.get(c.parentId) : undefined; return !!c && c.place === 'equip'; };
  const list = props.items.filter((i) => carried(i) && !i.bound && REFINABLE_SLOTS.includes(cat?.get(i.typeId)?.slot ?? ''))
    .sort((a, b) => (b.place === 'equip' ? 1 : 0) - (a.place === 'equip' ? 1 : 0));
  const [sel, setSel] = useState<number | null>(list[0]?.id ?? null);
  const [protect, setProtect] = useState(false);
  const [help, setHelp] = useState(false);
  const [last, setLast] = useState<LastResult[]>([]);

  const it = list.find((i) => i.id === sel);
  const t = it ? cat?.get(it.typeId) : undefined;
  const cur = it?.upgrade ?? 0, to = cur + 1;
  const maxed = to > REFINE_MAX;
  const frags = props.extras.fragments ?? 0;
  const stones = (props.extras.stones ?? 0) + Math.floor(frags / FRAGMENTS_PER_STONE);
  const canProtect = to <= REFINE_PROTECT_MAX && stones > 0;
  const useStone = protect && canProtect;
  const baseCost = maxed ? 0 : refineCost(to);
  const cost = baseCost * (useStone ? protectMult(to) : 1);
  const chance = maxed ? 0 : refineChance(to);
  const rk = risk(chance);
  const bonus = (u: number) => upgradeText(t?.slot, u);
  const afford = props.bank >= cost;

  // Último resultado: o servidor manda o resultado de cada tentativa (refineResult); guardamos as 5 últimas desta janela
  useEffect(() => {
    const r = props.result; if (!r) return;
    const ri = byId.get(r.itemId), rt = ri ? cat?.get(ri.typeId) : undefined;
    setLast((l) => (l[0]?.at === r.at ? l : [{ name: rt?.name ?? 'Item', typeId: ri?.typeId ?? 0, from: r.from, to: r.to, outcome: r.outcome, at: r.at }, ...l].slice(0, 5)));
  }, [props.result?.at]);

  const doRefine = () => {
    if (!it || !t) return;
    props.onRefine(it.id, useStone);
  };

  const heat = Math.min(1, cur / REFINE_MAX);   // o item brilha mais quanto maior o refine
  const lr = last[0];

  return (
    <div class="rn">
      {/* cabeçalho: a bigorna com brasas subindo */}
      <div class="rn-head">
        <div class="rn-forge" aria-hidden="true">
          <span class="rn-anvil">⚒️</span>
          <i class="rn-ember e1" /><i class="rn-ember e2" /><i class="rn-ember e3" /><i class="rn-ember e4" /><i class="rn-ember e5" />
        </div>
        <div class="rn-head-txt">
          <div class="rn-title">Forja de Refinamento
            <button class={'rn-help' + (help ? ' on' : '')} onClick={() => setHelp(!help)} title="Como funciona">?</button></div>
          <div class="rn-sub">Do <b>+1</b> ao <b>+{REFINE_MAX}</b> · a falha <b>nunca destrói</b> o item</div>
          <div class="rn-wallet">
            <span class="rn-chip" title="Pedras de proteção prontas"><i>🛡️</i><b>{props.extras.stones ?? 0}</b> pedra{(props.extras.stones ?? 0) === 1 ? '' : 's'}</span>
            <span class="rn-chip frag" title={`${FRAGMENTS_PER_STONE} fragmentos formam uma pedra (a conversão é automática ao usar)`}>
              <i>💠</i><b>{frags}</b>/{FRAGMENTS_PER_STONE}
              <span class="rn-fragbar"><em style={{ width: Math.min(100, (frags % FRAGMENTS_PER_STONE) / FRAGMENTS_PER_STONE * 100) + '%' }} /></span>
            </span>
            <span class="rn-chip gold" title="O refine é pago com o gold do banco"><i>🪙</i><b>{n(props.bank)}</b> gp</span>
          </div>
        </div>
      </div>

      {help && (
        <div class="rn-steps">
          <div><i>🗡️</i><b>1. Escolha o item</b><small>Só equipamentos de combate que estejam com você (equipados ou na mochila). Itens da loja não podem ser refinados.</small></div>
          <div><i>🔥</i><b>2. Veja chance e custo</b><small>Cada nível é mais difícil e mais caro: do 100% no +1 até 4% no +30. O custo sai do banco, com sucesso ou não.</small></div>
          <div><i>🛡️</i><b>3. Pedra de proteção?</b><small>Sem pedra, a falha tem {REFINE_DROP_CHANCE * 100}% de chance de derrubar um nível. Com pedra, o nível fica, mas o custo é x3 (x5 do +26 ao +30). A pedra só é gasta se falhar.</small></div>
          <div><i>💠</i><b>4. Junte fragmentos</b><small>{FRAGMENTS_PER_STONE} fragmentos formam uma pedra. Caem dos bosses das hunts e saem em fendas, roleta, eventos, Coliseu e tasks.</small></div>
        </div>
      )}

      <div class="rn-body">
        {/* lista dos equipamentos */}
        <div class="rn-list">
          <div class="rn-cap">Seus equipamentos</div>
          {list.map((i) => {
            const u = i.upgrade ?? 0;
            return (
              <button key={i.id} class={'rn-it' + (i.id === sel ? ' on' : '')} onClick={() => setSel(i.id)} title={cat?.get(i.typeId)?.name}>
                <span class="item-slot"><ItemImg typeId={i.typeId} count={1} /></span>
                <span class="rn-it-txt"><b>{cat?.get(i.typeId)?.name}</b><small>{i.place === 'equip' ? 'equipado' : 'na mochila'}</small></span>
                <span class={'rn-lv ' + (u ? bandOf(u).cls : 'b0')}>+{u}</span>
              </button>
            );
          })}
          {!list.length && <p class="muted small rn-empty">Nenhum equipamento de combate com você.</p>}
        </div>

        {/* bigorna: o item escolhido */}
        <div class="rn-detail">
          {!it || !t ? <p class="muted small rn-empty">Escolha um item na lista.</p> : <>
            <div class="rn-hero">
              <div class="rn-pedestal" style={{ '--heat': String(heat) } as any}>
                <span class="rn-big"><ItemImg typeId={it.typeId} count={1} /></span>
                <span class="rn-glow" />
              </div>
              <div class="rn-hero-txt">
                <b class="rn-name">{t.name}</b>
                <div class="rn-jump">
                  <span class={'rn-badge ' + (cur ? bandOf(cur).cls : 'b0')}>+{cur}</span>
                  {!maxed && <><span class="rn-arrow">➜</span><span class={'rn-badge next ' + bandOf(to).cls}>+{to}</span></>}
                </div>
                <small class="rn-band">{maxed ? 'Nível máximo alcançado' : `Faixa ${bandOf(to).name}`}</small>
              </div>
            </div>

            {maxed ? <div class="rn-max">⭐ Este item já está no <b>+{REFINE_MAX}</b>, o máximo da forja.<small>Bônus atual: {bonus(cur)}</small></div> : <>
              <div class="rn-bonus">
                <div><small>Agora</small><span>{cur ? bonus(cur) : 'sem bônus'}</span></div>
                <div class="up"><small>Com +{to}</small><span>{bonus(to)}</span></div>
              </div>

              {/* medidor da chance */}
              <div class={'rn-meter ' + rk}>
                <div class="rn-meter-top"><span>Chance de sucesso</span><b>{pct(chance)}</b><em>{RISK_TXT[rk]}</em></div>
                <div class="rn-gauge"><i style={{ width: chance + '%' }} /><span class="rn-tick t20" /><span class="rn-tick t50" /></div>
              </div>

              {/* pedra de proteção */}
              <div class="rn-protect">
                <button class={'rn-opt' + (!useStone ? ' on' : '')} onClick={() => setProtect(false)}>
                  <b>Sem pedra</b>
                  <small>Custo normal ({n(baseCost)} gp)</small>
                  <span class="rn-fail bad">Se falhar: {cur > 0 ? <>{REFINE_DROP_CHANCE * 100}% de chance de cair para <b>+{cur - 1}</b></> : <>continua <b>+0</b></>}</span>
                </button>
                <button class={'rn-opt stone' + (useStone ? ' on' : '')} disabled={!canProtect} onClick={() => setProtect(true)}
                  title={canProtect ? '' : 'Você não tem pedra de proteção nem 10 fragmentos'}>
                  <b>🛡️ Com pedra</b>
                  <small>Custo x{protectMult(to)} ({n(baseCost * protectMult(to))} gp)</small>
                  <span class="rn-fail good">{canProtect ? <>Se falhar: mantém <b>+{cur}</b> e gasta 1 pedra</> : 'Você não tem pedra (nem 10 fragmentos)'}</span>
                </button>
              </div>

              {/* custo e saldo */}
              <div class="rn-pay">
                <div class="rn-cost"><small>Custo</small><b>{n(cost)} gp</b></div>
                <div class="rn-cost"><small>Banco</small><b class={afford ? '' : 'rn-red'}>{n(props.bank)} gp</b></div>
                <div class="rn-cost"><small>{afford ? 'Depois' : 'Faltam'}</small><b class={afford ? 'rn-dim' : 'rn-red'}>{n(Math.abs(props.bank - cost))} gp</b></div>
                <button class="tb-button green rn-go" disabled={!afford} onClick={doRefine}>🔨 Refinar +{to}</button>
              </div>
            </>}
          </>}

          {/* último resultado (deduzido nesta sessão) */}
          {lr && (
            <div class={'rn-result ' + lr.outcome} key={lr.at}>
              <span class="item-slot"><ItemImg typeId={lr.typeId} count={1} /></span>
              <div>
                <b>{lr.outcome === 'ok' ? `Sucesso! ${lr.name} agora é +${lr.to}` : lr.outcome === 'drop' ? `Falhou: ${lr.name} caiu para +${lr.to}` :
                  lr.outcome === 'saved' ? `Falhou, mas a pedra segurou o +${lr.to}` : `Falhou, mas ${lr.name} continua +${lr.to}`}</b>
                {last.length > 1 && <small class="rn-hist">Antes: {last.slice(1).map((r) => <span key={r.at} class={r.outcome}>{r.outcome === 'ok' ? '▲' : r.outcome === 'drop' ? '▼' : '■'}+{r.to}</span>)}</small>}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* escala +1..+30 */}
      <div class="rn-scale-cap">Escala da forja <small class="muted">· passe o mouse para ver chance e custo de cada nível</small></div>
      <div class="rn-scale">
        {Array.from({ length: REFINE_MAX }, (_, k) => {
          const lv = k + 1, b = bandOf(lv);
          const st = it ? (lv <= cur ? ' done' : lv === to ? ' next' : '') : '';
          return (
            <span key={lv} class={'rn-pip ' + b.cls + st + (it && lv === cur ? ' cur' : '')}
              title={`+${lv} (${b.name}): chance ${pct(refineChance(lv))} · ${short(refineCost(lv))} gp${lv > 25 ? ' · pedra x5' : ' · pedra x3'}`}>
              <em>{lv}</em>
            </span>
          );
        })}
      </div>
      <div class="rn-bands">
        {BANDS.map((b) => <span key={b.cls} class={b.cls} style={{ flex: b.to - b.from + 1 }}>{b.name} <small>{pct(refineChance(b.from))}–{pct(refineChance(b.to))}</small></span>)}
      </div>
    </div>
  );
}
